import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  addMonths, subMonths, format, isAfter, isBefore,
  isSameMonth, isBefore as dateIsBefore
} from 'date-fns';

import './Availability.css';
import { LanguageContext } from "../context/LanguageContext";

const API_URL = process.env.REACT_APP_API_URL;

const AvailabilityForm = () => {
  const { objectId } = useParams();

  // ⭐ Multilingual
  const { texts, locale } = useContext(LanguageContext);

  const [baseMonth, setBaseMonth] = useState(new Date());
  const [message, setMessage] = useState('');
  const [availabilityStatus, setAvailabilityStatus] = useState({});
  const [selectionMode, setSelectionMode] = useState('available');
  const [selectionStart, setSelectionStart] = useState(null);

  const isSubmittingRef = useRef(false);

  // ------------------------------------------------------------
  // Verfügbarkeiten laden
  // ------------------------------------------------------------
  useEffect(() => {
    if (!objectId) return;

    fetch(`${API_URL}/object/${objectId}/availability`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        const mapped = {};
        data.forEach(({ start_date, end_date, id }) => {
          let current = new Date(start_date);
          const end = new Date(end_date);
          while (!isAfter(current, end)) {
            const key = format(current, 'yyyy-MM-dd');
            mapped[key] = { status: 'available', id };
            current.setDate(current.getDate() + 1);
          }
        });
        setAvailabilityStatus(mapped);
      })
      .catch(console.error);
  }, [objectId]);

  const handlePrev = () => setBaseMonth((prev) => subMonths(prev, 1));
  const handleNext = () => setBaseMonth((prev) => addMonths(prev, 1));

  const handleDateClick = (date) => {
    if (dateIsBefore(date, new Date())) return;

    if (!selectionStart) {
      setSelectionStart(date);
      return;
    }

    const rangeStart = isBefore(selectionStart, date) ? selectionStart : date;
    const rangeEnd = isAfter(selectionStart, date) ? selectionStart : date;

    setAvailabilityStatus((prev) => {
      const newStatus = { ...prev };
      let current = new Date(rangeStart);

      while (!isAfter(current, rangeEnd)) {
        const key = format(current, 'yyyy-MM-dd');

        newStatus[key] = {
          status: selectionMode,
          id: prev[key]?.id || null,
        };

        current.setDate(current.getDate() + 1);
      }

      // direkt speichern
      handleSubmit(newStatus);
      return newStatus;
    });

    setSelectionStart(null);
  };

  // ------------------------------------------------------------
  // saveRanges (unverändert)
  // ------------------------------------------------------------
  function saveRanges(dates, status) {
    if (dates.length === 0) return [];

    const sorted = dates.map(d => ({ ...d, date: new Date(d.date) }))
      .sort((a, b) => a.date - b.date);

    const ranges = [];
    let currentRange = [sorted[0]];

    for (let i = 1; i < sorted.length; i++) {
      const prevDate = new Date(currentRange[currentRange.length - 1].date);
      const currentDate = new Date(sorted[i].date);
      const sameId = sorted[i].id === currentRange[0].id;

      prevDate.setDate(prevDate.getDate() + 1);

      if (prevDate.toDateString() === currentDate.toDateString() && sameId) {
        currentRange.push(sorted[i]);
      } else {
        ranges.push([...currentRange]);
        currentRange = [sorted[i]];
      }
    }

    if (currentRange.length) ranges.push(currentRange);

    const mergedRanges = [];

    for (const group of ranges) {
      const startDate = group[0].date;
      const endDate = group[group.length - 1].date;

      const start_date = format(startDate, 'yyyy-MM-dd');
      const end_date = format(endDate, 'yyyy-MM-dd');

      const last = mergedRanges[mergedRanges.length - 1];

      const isMergeable =
        last &&
        new Date(last.end_date).getTime() + 86400000 >= new Date(start_date).getTime();

      if (isMergeable) {
        last.end_date = end_date;
      } else {
        mergedRanges.push({ start_date, end_date, id: null, status });
      }
    }
    return mergedRanges;
  }

  const handleSubmit = async (statusMap) => {
    if (!objectId) {
      setMessage(texts.availabilityManual.errorNoObjectId);
      return;
    }

    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    const availability = statusMap || availabilityStatus;

    const grouped = {};
    const deletedIdsSet = new Set();
    const partialDeletes = {};

    Object.entries(availability).forEach(([dateStr, val]) => {
      if (!val) return;

      if (val.status === 'delete' && val.id) {
        deletedIdsSet.add(val.id);
        if (!partialDeletes[val.id]) partialDeletes[val.id] = [];
        partialDeletes[val.id].push(dateStr);
      } else {
        if (!grouped[val.status]) grouped[val.status] = [];
        grouped[val.status].push({ date: dateStr, id: val.id });
      }
    });

    const toSave = saveRanges(
      (grouped['available'] || []).filter(entry => !deletedIdsSet.has(entry.id)),
      'available'
    );

    let allSuccessful = true;

    try {
      // 1) Teilweise gelöschte Blöcke: löschen -> splitten -> neu anlegen
      for (const id of deletedIdsSet) {
        const deletedDates = new Set(partialDeletes[id] || []);

        const deleteResponse = await fetch(`${API_URL}/object/${objectId}/availability/${id}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        });

        if (!deleteResponse.ok) {
          const error = await deleteResponse.json().catch(() => ({ message: 'unknown' }));
          console.error("Fehler beim Löschen:", error.message);
          allSuccessful = false;
          continue;
        }

        const remainingDates = Object.entries(availability)
          .filter(([dateStr, val]) =>
            val.id === id &&
            val.status === 'available' &&
            !deletedDates.has(dateStr)
          )
          .map(([dateStr]) => ({ date: dateStr, id: null }));

        const newRanges = saveRanges(remainingDates, 'available');

        for (const { start_date, end_date } of newRanges) {
          const response = await fetch(`${API_URL}/object/${objectId}/availability/manual`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('authToken')}`,
            },
            body: JSON.stringify({ start_date, end_date }),
          });

          if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'unknown' }));
            console.error("Fehler beim Speichern (Split):", error.message);
            allSuccessful = false;
          }
        }
      }

      // 2) Für jede toSave-Range: angrenzende/überlappende bestehende Blöcke löschen, dann zusammengefasst POST
      for (const { start_date, end_date } of toSave) {
        const newStart = new Date(start_date);
        const newEnd = new Date(end_date);

        const idsToRemove = new Set();
        Object.entries(availability).forEach(([dateStr, val]) => {
          if (!val || !val.id || val.status !== 'available') return;
          const d = new Date(dateStr);
          const beforeWindow = new Date(newStart); beforeWindow.setDate(beforeWindow.getDate() - 1);
          const afterWindow = new Date(newEnd); afterWindow.setDate(afterWindow.getDate() + 1);
          if (d >= beforeWindow && d <= afterWindow) idsToRemove.add(val.id);
        });

        const localCopy = { ...availability };

        let deleteFailed = false;
        for (const idToDel of idsToRemove) {
          const deleteResp = await fetch(`${API_URL}/object/${objectId}/availability/${idToDel}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
          });

          if (!deleteResp.ok) {
            const err = await deleteResp.json().catch(() => ({ message: 'unknown' }));
            console.error("Fehler beim Löschen angrenzender Blocks:", err.message);
            allSuccessful = false;
            deleteFailed = true;
            break;
          }

          Object.entries(localCopy).forEach(([dateStr, val]) => {
            if (!val) return;
            if (val.id === idToDel) {
              localCopy[dateStr] = { ...val, id: null };
            }
          });
        }

        if (deleteFailed) {
          try {
            const fresh = await fetch(`${API_URL}/object/${objectId}/availability`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
            });
            const freshData = await fresh.json();
            const mapped = {};
            freshData.forEach(({ start_date: s, end_date: e, id }) => {
              let cur = new Date(s);
              const end = new Date(e);
              while (!isAfter(cur, end)) {
                mapped[format(cur, 'yyyy-MM-dd')] = { status: 'available', id };
                cur.setDate(cur.getDate() + 1);
              }
            });
            setAvailabilityStatus(mapped);
          } catch (e) {
            console.error("Refetch failed:", e);
          }
          continue;
        }

        const mergedDates = Object.entries(localCopy)
          .filter(([dateStr, val]) => val && val.status === 'available')
          .map(([dateStr]) => new Date(dateStr))
          .filter(d => d >= new Date(start_date) && d <= new Date(end_date))
          .sort((a, b) => a - b);

        let finalStartDate = mergedDates.length ? mergedDates[0] : newStart;
        let finalEndDate = mergedDates.length ? mergedDates[mergedDates.length - 1] : newEnd;

        const finalStart = format(finalStartDate, 'yyyy-MM-dd');
        const finalEnd = format(finalEndDate, 'yyyy-MM-dd');

        const resp = await fetch(`${API_URL}/object/${objectId}/availability/manual`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
          body: JSON.stringify({ start_date: finalStart, end_date: finalEnd }),
        });

        if (!resp.ok) {
          const error = await resp.json().catch(() => ({ message: 'unknown' }));
          console.error("Fehler beim Speichern (zusammengefasst):", error.message);
          allSuccessful = false;

          try {
            const fresh = await fetch(`${API_URL}/object/${objectId}/availability`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
            });
            const freshData = await fresh.json();
            const mapped = {};
            freshData.forEach(({ start_date: s, end_date: e, id }) => {
              let cur = new Date(s);
              const end = new Date(e);
              while (!isAfter(cur, end)) {
                mapped[format(cur, 'yyyy-MM-dd')] = { status: 'available', id };
                cur.setDate(cur.getDate() + 1);
              }
            });
            setAvailabilityStatus(mapped);
          } catch (e) {
            console.error("Refetch failed after POST error:", e);
          }
        } else {
          Object.entries(localCopy).forEach(([dateStr, val]) => {
            if (!val) return;
            const d = new Date(dateStr);
            if (d >= new Date(finalStart) && d <= new Date(finalEnd)) {
              localCopy[dateStr] = { status: 'available', id: null };
            }
          });
          setAvailabilityStatus(localCopy);
        }
      }

      try {
        const fresh = await fetch(`${API_URL}/object/${objectId}/availability`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
        });
        const freshData = await fresh.json();
        const mapped = {};
        freshData.forEach(({ start_date: s, end_date: e, id }) => {
          let cur = new Date(s);
          const end = new Date(e);
          while (!isAfter(cur, end)) {
            mapped[format(cur, 'yyyy-MM-dd')] = { status: 'available', id };
            cur.setDate(cur.getDate() + 1);
          }
        });
        setAvailabilityStatus(mapped);
      } catch (e) {
        console.error("Final refetch failed:", e);
      }

      setMessage(
        allSuccessful
          ? texts.availabilityManual.savedSuccess
          : texts.availabilityManual.savedPartialError
      );
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error("Unerwarteter Fehler:", err);
      setMessage(texts.availabilityManual.errorSaving);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const renderCalendar = (monthOffset) => {
    const monthDate = addMonths(baseMonth, monthOffset);
    const allDates = Object.entries(availabilityStatus);

    const availableDates = allDates
      .filter(([, val]) => val.status === 'available')
      .map(([date]) => new Date(date));

      return (
        <div key={monthOffset} className="calendar-container">
          <h3>{format(monthDate, 'MMMM yyyy', { locale })}</h3>

          <DatePicker
            inline
            locale={locale}
            calendarStartDay={1}
            openToDate={monthDate}
            highlightDates={[
              { 'react-datepicker__day--highlighted-custom-1': availableDates },
            ]}
            onChange={(date) => handleDateClick(date)}
            dayClassName={(date) => {
              const key = format(date, 'yyyy-MM-dd');
              const isInMonth = isSameMonth(date, monthDate);

              if (dateIsBefore(date, new Date()) || !isInMonth)
                return 'react-datepicker__day--disabled';

              if (availabilityStatus[key]?.status === 'available')
                return 'highlighted-custom-1';

              return '';
            }}
            renderCustomHeader={() => null}
          />
        </div>
      );
  };

    if (!objectId) {
      return <div>⏳ {texts.availabilityManual.loadingObject}</div>;
    }

    return (
      <div className="tab-content">
        <h2>{texts.availabilityManual.editAvailability}</h2>

        <div className="navigation-row">
          <button onClick={handlePrev}>&lt;</button>

          <span>
            {format(baseMonth, 'MMMM yyyy', { locale })} –{' '}
            {format(addMonths(baseMonth, 2), 'MMMM yyyy', { locale })}
          </span>

          <button onClick={handleNext}>&gt;</button>
        </div>

        <div className="legend-toggle">
          <button
            type="button"
            className={`legend-button ${selectionMode === 'available' ? 'active' : ''}`}
            onClick={() => {
              setSelectionMode('available');
              setSelectionStart(null);
            }}
          >
            ✓ {texts.availabilityManual.legendAvailable}
          </button>

          <button
            type="button"
            className={`legend-button ${selectionMode === 'delete' ? 'active' : ''}`}
            onClick={() => {
              setSelectionMode('delete');
              setSelectionStart(null);
            }}
          >
            🗑 {texts.availabilityManual.legendDelete}
          </button>
        </div>

        <div className="calendar-row">
          {[0, 1, 2].map((offset) => renderCalendar(offset))}
        </div>

        {message && <div className="message">{message}</div>}
      </div>
    );

};

export default AvailabilityForm;
